import { Summary } from '../context/cart'
import { StrapiProduct } from '../data/use-products/types'
import { PatientSubscriptionPaymentStatus, PatientSubscriptionStatus } from '../subscriptions/types'

export type UUID = string

export type PasswordStrength = 0 | 1 | 2 | 3

export type ConsultationContactMethod = 'phone' | 'video'

export enum Sex {
  Male = 'Male',
  Female = 'Female',
}

export type Profile = {
  firstName: string
  lastName: string
  dateOfBirth: string
  sex: Sex
  phone?: string
  password?: string
  genderInfo: string | null
  emrId?: string
  email?: string
  cpfData?: CPFData
  nationalIdentificationNumber?: {
    documentNumber: string
    documentType: string
  }
  hasBookedFirstConsultation?: boolean
  firstConsultationCarriedOutDate?: string
}

export enum Role {
  PATIENT = 'patient',
  CLINICIAN = 'clinician',
  CLINICAL_MANAGER = 'clinical-manager',
  CLINICIAN_AND_CLINICAL_MANAGER = 'clinician-and-clinical-manager',
  MACHINE = 'machine',
}

export type SocialData = {
  provider: string
  phoneNumber: string
  firstName: string
  lastName: string
  email: string
  birthday: {
    day: string
    month: string
    year: string
  }
  gender: string
}

export type CPFData = {
  code: string
}

export type CompleteUser = StrapiUser & Profile

export interface B2BProductDiscount {
  id: number
  discount: number
  product: Pick<StrapiProduct, 'id'>
}

export interface B2BCompany {
  id: number
  companyId: string
  companyName: string
  secret: string
  slug: string
  productDiscounts?: B2BProductDiscount[]
}

export interface Subscription {
  id: number
  subscriptionId: string
  amount: number
  type: string
  status: PatientSubscriptionStatus
  cancelAtPeriodEnd?: boolean
  lastPaymentId?: string
  lastPaymentStatus?: PatientSubscriptionPaymentStatus
  currentPeriodStart?: number
  currentPeriodEnd?: number
  nextPaymentAt?: number
}

export type StrapiUser = {
  id: number
  username: string
  email: string
  phone: string
  provider: 'local' | 'google' | 'facebook'
  confirmed: boolean
  blocked: boolean
  createdAt: string
  updatedAt: string
  phoneVerified: boolean
  emailVerified: boolean
  emrId: string
  cardoId: string | null
  prescriptionUserId: string | null
  prescriptionRegistrationError: string | null
  uuid: string
  isClinician: boolean
  isDependant: boolean
  cpfData: CPFData
  homeAddress?: Address
  dependants?: CompleteUser[]
  holder?: StrapiUser
  b2bCompany?: B2BCompany | null
  subscription?: Subscription | null
  showDebugInfo?: boolean
  hasBookedFirstConsultation?: boolean
}

export enum OrderStatus {
  PROCESSING = 'PROCESSING',
  FAILED = 'FAILED',
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
}

export enum ConsultationStatus {
  QUEUED = 'QUEUED',
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  CLOSED_WITH_ERROR = 'CLOSED_WITH_ERROR',
  CLOSING = 'CLOSING',
  CANCELLING = 'CANCELLING',
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
}

export enum PatientConsultationStatus {
  PROCESSING = 'PROCESSING',
  ORDER_CANCELLED = 'ORDER_CANCELLED',
  QUEUED = 'QUEUED',
  ACTIVE = 'ACTIVE',
  COMPLETE = 'COMPLETE',
  CLOSED = 'CLOSED',
  CLOSED_WITH_ERROR = 'CLOSED_WITH_ERROR',
  CANCELLING = 'CANCELLING',
  CANCELLED = 'CANCELLED',
}

export enum OrderFailureDomain {
  PAYMENT = 'Payment',
}

export enum SecurityCode {
  CVV = 'CVV',
  CVC = 'CVC',
}

export type Symptom = {
  question: string
  answer: string
}

export type Clinician = {
  id: number
  firstName: string
  lastName: string
  professionTitle?: string
  specialtyAndSubSpecialtyTitle?: string
  profilePictureUrl?: string
}

export type DateSelectionType = {
  id: string
  label: string
  approximateYear: boolean
  dates: {
    from: string | null
    to: string | null
  }
  comments?: string
}

export type Diagnosis = {
  id: string
  label: string
}

export enum Administration {
  INHALED = 'inhaled',
  INTRAARTICULAR = 'intra-articular',
  INTRAMUSCULAR = 'intramuscular',
  INTRAVAGINAL = 'intravaginal',
  INTRAVENOUS = 'intravenous',
  NASAL = 'nasal',
  OPHTHALMIC = 'ophthalmic',
  ORAL = 'oral',
  OPTICAL = 'optical',
  PARENTERAL = 'parenteral',
  RECTAL = 'rectal',
  SD = 'S/D',
  SUBCUTANEOUS = 'subcutaneous',
  SUBLINGUAL = 'sublingual',
  TOPIC = 'topic',
  VAGINAL = 'vaginal',
}

export enum PrescriptionMedicalHistoryType {
  CURRENT = 'current',
  PAST = 'past',
}

export type Prescription = {
  diagnosis?: Diagnosis
  medication: {
    uuid?: string
    id: string
    label: string
  }
  quantity: number | null
  duration: number | null
  administration: Administration
  instructions: string
  forbidSubstitute: boolean
  medicalHistory?: {
    type: PrescriptionMedicalHistoryType
    date: Date | null
    approximateYear: boolean
  }
  encounterUuid: string
  emrId?: string
  prescriptionId: string
  prescriptionUrl?: string
  securityCode?: string
}

type WeightHistory = {
  weightProblemStart?: string
  weightGainTrigger?: string
  previousWeightLossTreatments?: string
  dietaryFactorsContributingToProblem?: string
  weightGoal?: string
  currentDiet?: string
}

export type MedicalHistory = {
  allergies?: {
    drugAllergies?: DateSelectionType[]
    foodIntolerancesOrDietaryRestrictions?: string
    otherWarning?: string
  }
  nonPathologicalPersonalHistory?: {
    familyHistoryDiseases?: string
    physiologicalAndSocialBackground?: string
    toxicBackground?: string
  }
  pathologicalPersonalHistory?: {
    previousBackground?: string
    previousDiseases?: DateSelectionType[]
    previousSurgeries?: DateSelectionType[]
    currentMedication?: DateSelectionType[]
  }
  weightHistory: WeightHistory
}

export type JournalData = {
  additionalInformation?: {
    curp?: string
    address?: string
    weight?: number
    height?: number
  }
  medicalHistory?: MedicalHistory
  journal?: {
    currentSituation?: string
    status?: string
    assessment?: string
    diagnosis?: {
      id: string
      label: string
    }[]
    plannedActions?: string
  }
  prescriptions?: Prescription[]
}

export type Patient = {
  dateOfBirth: string
  emrId: string
  firstName: string
  lastName: string
  sex: Sex
  cardoId: string
}

export type StrapiFileInfo = {
  id: string
  mime: string
  name: string
  url: string
  uploadedAt?: string
}

export type StrapiImage = {
  alternativeText?: string
  ext: string
  url: string
  hash: string
  mime: string
  name: string
  size: number
  width: number
  height: number
}

export enum ConsultationActionType {
  UNASSIGNED = 'UNASSIGNED',
  ASSIGNED = 'ASSIGNED',
  QUEUED = 'QUEUED',
  VIDEO_CALL_STARTED = 'VIDEO_CALL_STARTED',
  PHONE_CALL_STARTED = 'PHONE_CALL_STARTED',
  VIDEO_CALL_FAILED = 'VIDEO_CALL_FAILED',
  PHONE_CALL_FAILED = 'PHONE_CALL_FAILED',
  VIDEO_CALL_ENDED = 'VIDEO_CALL_ENDED',
  PHONE_CALL_ENDED = 'PHONE_CALL_ENDED',
  VIDEO_CALL_COMPLETED = 'VIDEO_CALL_COMPLETED',
  PHONE_CALL_COMPLETED = 'PHONE_CALL_COMPLETED',
  VIDEO_CALL_CANCELLED = 'VIDEO_CALL_CANCELLED',
  PHONE_CALL_CANCELLED = 'PHONE_CALL_CANCELLED',
  CLOSED = 'CLOSED',
  CLOSED_WITH_ERROR = 'CLOSED_WITH_ERROR',
  CANCELLED = 'CANCELLED',
}

export type ConsultationAction = {
  createdAt: string
  type: ConsultationActionType
  user: { id: number; email: string; role: Role }
}

export enum ConsultationTimeStatus {
  PAST = 'past',
  UPCOMING = 'upcoming',
}

export enum ConsultationType {
  SCHEDULED = 'scheduled',
  ON_DEMAND = 'on-demand',
  MEDICAL_UPDATE = 'medical-update',
}

export type Consultation = {
  id: number
  patientId: number
  createdAt: string
  updatedAt: string
  clinicianId?: number
  symptomsDocuments?: Array<StrapiFileInfo> | null
  clinician?: Clinician
  position: number | null
  status: ConsultationStatus
  patient?: Patient
  symptoms?: Symptom[]
  summary?: Summary
  journal?: JournalData
  prescriptionId?: string
  videoCallId?: number
  preferredContactMethod: PreferredContactMethod
  product: {
    id: number
    name: string
    imageUri: string
    slug: string
  }
  order: {
    id: number
    uuid: string
    amount: number
    maskedCardNumber: string
    cardType: string
  }
  actions: ConsultationAction[]
  startTime: number
  endTime: number
  type: ConsultationType
}

export enum PaymentMethodId {
  VISA = 'visa',
  MASTER = 'master',
  AMEX = 'amex',
  PIX = 'pix',
}

export enum PreferredContactMethod {
  PHONE = 'phone',
  VIDEO = 'video',
}

declare global {
  interface Window {
    zE: any // TODO: find types for zendesk sdk
    dataLayer?: Record<string, unknown>[]
    OneTrust?: {
      ToggleInfoDisplay: () => void
    }
    MP_DEVICE_SESSION_ID?: string
  }
}

export type CreditCardType = {
  niceType: string
  type: string
  patterns: number[] | [number[]]
  gaps: number[]
  lengths: number[]
  code: {
    size: number
    name: string
  }
  matchStrength?: number
}

export enum Reason {
  SIGN_IN = 'signin',
  SIGN_UP = 'signup',
}

export enum CheckoutAlertType {
  ADD_DISCOUNT = 'addDiscount',
  ADD_FULL_DISCOUNT = 'addFullDiscount',
  LOGIN = 'login',
  NEW_ACCOUNT = 'newAccount',
  REMOVE_DISCOUNT = 'removeDiscount',
}

export type CheckoutAlertDiscountType =
  | CheckoutAlertType.ADD_DISCOUNT
  | CheckoutAlertType.ADD_FULL_DISCOUNT
  | CheckoutAlertType.REMOVE_DISCOUNT

export enum DiscountType {
  PROMO_CODE = 'promoCode',
  ANONYMOUS_PROMO_CODE = 'anonymousPromoCode',
  REFERRAL = 'referral',
  B2B_COMPANY_EMPLOYEE = 'b2bCompanyEmployee',
}

export enum DiscountUnitType {
  PERCENTAGE = 'percentage',
}

export type Address = {
  zipCode: string
  cityName: string
  stateName: string
  streetName: string
  streetNumber: string
  district: string
  comments?: string
}
